import {
  ProviderUnavailable,
  type SpeakOpts,
  type SpeakResult,
  type TtsContext,
  type TtsProvider,
} from '../TtsProvider';

/**
 * The floor of the chain: the platform's own voice.
 *
 * Phase 0 corrected the design doc in **both** directions here. `getVoices()` returned 68 voices,
 * so the widely-reported "only Eloquence is exposed" limitation does not apply to this device, and
 * the default `de-DE` voice started speaking in 93 ms — genuinely fast. But the per-voice
 * measurement was invalid: `speechSynthesis` queues, and the second utterance was requested while
 * the first was still speaking, so its apparent 5-second start was queue wait. Hence `cancel()`
 * before every utterance here.
 *
 * So this is last on **quality** grounds, which are still unassessed, not on availability or
 * latency grounds. Do not treat it as broken.
 *
 * It is also the one provider that cannot serve a timed activation: `speak()` outside a user
 * gesture is silently dropped, and a dwell timer or auto-scan selection is not a gesture. That is
 * what `ctx.inGesture` is for, and getting it right here is what stops Phase 3 producing silent
 * failures.
 */

const LANG = 'de-DE';

/**
 * Two deadlines, because there are two failures and one budget cannot cover both (`SPK-17`, C81).
 *
 * This provider used to resolve only on `end` and reject only on `error`. An utterance iOS silently
 * drops does neither, so `speak()` never settled — and because the next `speak()` calls `cancel()`
 * first, the *second* tap aborted the first and worked. "Nicht auf Anhieb", precisely.
 *
 * A single budget long enough for a long sentence is far too long to notice a dropped one, and a single
 * budget short enough to notice a dropped one would cut a long sentence off mid-word. So: a short wait
 * for `start`, then a length-scaled wait for `end`.
 */
const START_BUDGET_MS = 1200;
const END_BUDGET_BASE_MS = 2000;
const END_BUDGET_PER_CHAR_MS = 120;
const END_BUDGET_CAP_MS = 20_000;

export function endBudgetMs(chars: number): number {
  return Math.min(END_BUDGET_CAP_MS, END_BUDGET_BASE_MS + chars * END_BUDGET_PER_CHAR_MS);
}

async function voicesReady(timeoutMs = 1000): Promise<SpeechSynthesisVoice[]> {
  const existing = speechSynthesis.getVoices();
  if (existing.length > 0) return existing;

  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      speechSynthesis.removeEventListener('voiceschanged', done);
      resolve(speechSynthesis.getVoices());
    };
    const timer = setTimeout(done, timeoutMs);
    speechSynthesis.addEventListener('voiceschanged', done, { once: true });
  });
}

export function createWebSpeechProvider(): TtsProvider {
  return {
    id: 'webSpeech',

    isAvailable(ctx: TtsContext): boolean {
      if (typeof speechSynthesis === 'undefined') return false;
      // Not `audioUnlocked`: this path does not use our AudioContext at all. It needs a gesture.
      return ctx.inGesture;
    },

    async speak(text: string, opts: SpeakOpts): Promise<SpeakResult> {
      const startedAtMs = performance.now();
      if (typeof speechSynthesis === 'undefined') {
        throw new ProviderUnavailable('webSpeech', 'speechSynthesis unavailable');
      }

      const voices = await voicesReady();
      const voice = voices.find((v) => v.lang === LANG) ?? voices.find((v) => v.lang.startsWith('de'));

      return new Promise<SpeakResult>((resolve, reject) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = LANG;
        if (voice) utterance.voice = voice;

        let timer: ReturnType<typeof setTimeout>;
        let audioAtMs: number | undefined;

        const onAbort = (): void => {
          speechSynthesis.cancel();
          cleanup();
          reject(new ProviderUnavailable('webSpeech', 'aborted'));
        };

        const cleanup = (): void => {
          clearTimeout(timer);
          opts.signal.removeEventListener('abort', onAbort);
        };

        /**
         * Give up on the clock, and **cancel** on the way out.
         *
         * Without the cancel, a zombie utterance stays queued and arrives after the moment has passed —
         * which this file's own header already names as worse than a dropped one.
         */
        const giveUp = (reason: string): void => {
          cleanup();
          speechSynthesis.cancel();
          reject(new ProviderUnavailable('webSpeech', reason));
        };

        timer = setTimeout(() => giveUp('webspeech_no_start'), START_BUDGET_MS);

        utterance.addEventListener('start', () => {
          // It is speaking, so this is time to first audio for this provider — the one thing the
          // Web Audio path gets from `playBuffer` and this path had no source for.
          audioAtMs = performance.now();
          clearTimeout(timer);
          timer = setTimeout(() => giveUp('webspeech_no_end'), endBudgetMs(text.length));
        });

        utterance.addEventListener('end', () => {
          cleanup();
          resolve({
            providerId: 'webSpeech',
            startedAtMs,
            cached: false,
            ...(audioAtMs === undefined ? {} : { audioAtMs }),
          });
        });

        utterance.addEventListener('error', (event) => {
          cleanup();
          reject(new ProviderUnavailable('webSpeech', event.error ?? 'utterance error'));
        });

        opts.signal.addEventListener('abort', onAbort, { once: true });

        // Always cancel first. Phase 0's invalid measurement was caused by queueing, and a queued
        // utterance in a conversation aid is worse than a dropped one: it arrives after the moment
        // has passed.
        speechSynthesis.cancel();
        speechSynthesis.speak(utterance);
      });
    },
  };
}
